import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar } from './ui/calendar';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';

// Define DateRange type locally (react-datepicker doesn't export this)
type DateRange = { from: Date | undefined; to?: Date | undefined };

interface DateFilterBarProps {
  selectedDateRange: string;
  customStartDate?: string;
  customEndDate?: string;
  onPresetSelect: (preset: string) => void;
  onCustomRangeQuery: (startDate: string, endDate: string) => void;
}

// Helper to parse YYYY-MM-DD string in local timezone (not UTC)
// Creates date at noon to avoid timezone boundary issues
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0); // month is 0-indexed, time at noon
};

// Primary presets shown as buttons
const PRIMARY_PRESETS = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week_to_date', label: 'Week to Date' },
  { value: 'last_week_sun_sat', label: 'Last Week' },
];

// Secondary presets shown in "More" dropdown
const MORE_PRESETS = [
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month_to_yesterday', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'year_to_date', label: 'Year to Date' },
];

export function DateFilterBar({
  selectedDateRange,
  customStartDate,
  customEndDate,
  onPresetSelect,
  onCustomRangeQuery,
}: DateFilterBarProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    // Initialize with existing custom dates if available
    if (customStartDate && customEndDate) {
      return {
        from: parseLocalDate(customStartDate),
        to: parseLocalDate(customEndDate),
      };
    }
    return undefined;
  });

  const isCustomRange = selectedDateRange === 'custom';

  // Check if selected preset is in the "More" dropdown
  const isMorePresetSelected = MORE_PRESETS.some(p => p.value === selectedDateRange);
  const selectedMorePreset = MORE_PRESETS.find(p => p.value === selectedDateRange);

  const handlePresetClick = (preset: string) => {
    onPresetSelect(preset);
  };

  const handleMoreSelect = (value: string) => {
    onPresetSelect(value);
  };

  const handleRunLiveQuery = () => {
    if (dateRange?.from && dateRange?.to) {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');
      onCustomRangeQuery(startDate, endDate);
      setCalendarOpen(false);
    }
  };

  // Format the custom date range display (American style)
  const formatCustomRangeDisplay = () => {
    if (customStartDate && customEndDate) {
      const startDate = parseLocalDate(customStartDate);
      const endDate = parseLocalDate(customEndDate);

      // Check if same month and year (e.g., "Jan 4-10, 2026")
      if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
        return `${format(startDate, 'MMM d')}-${format(endDate, 'd, yyyy')}`;
      }

      // Check if same year but different month (e.g., "Jan 4 - Feb 7, 2026")
      if (startDate.getFullYear() === endDate.getFullYear()) {
        return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
      }

      // Different years (e.g., "Dec 28, 2025 - Jan 7, 2026")
      return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    return 'Custom Range';
  };

  return (
    <div className="flex items-center gap-2">
      {/* Primary preset buttons */}
      {PRIMARY_PRESETS.map((preset) => (
        <Button
          key={preset.value}
          variant={selectedDateRange === preset.value ? 'default' : 'outline'}
          size="sm"
          onClick={() => handlePresetClick(preset.value)}
        >
          {preset.label}
        </Button>
      ))}

      {/* More dropdown */}
      <Select
        value={isMorePresetSelected ? selectedDateRange : ''}
        onValueChange={handleMoreSelect}
      >
        <SelectTrigger className="w-[120px] h-8 text-sm">
          <SelectValue placeholder="More...">
            {selectedMorePreset ? selectedMorePreset.label : 'More...'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {MORE_PRESETS.map((preset) => (
            <SelectItem key={preset.value} value={preset.value}>
              {preset.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Custom Range button with popover */}
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={isCustomRange ? 'default' : 'outline'}
            size="sm"
            className="gap-1"
          >
            <CalendarIcon className="h-4 w-4" />
            {isCustomRange ? formatCustomRangeDisplay() : 'Custom Range'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <div className="p-4 space-y-4">
            <div className="text-sm font-medium">Select Date Range</div>
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              defaultMonth={dateRange?.from || (() => {
                // Create current date at noon to avoid timezone boundary issues
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
              })()}
              showOutsideDays={false}
            />
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-sm text-muted-foreground">
                {dateRange?.from && dateRange?.to ? (
                  <>
                    {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                  </>
                ) : (
                  'Pick a start and end date'
                )}
              </div>
              <Button
                size="sm"
                onClick={handleRunLiveQuery}
                disabled={!dateRange?.from || !dateRange?.to}
              >
                Run Live Query
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Live badge when custom range is active */}
      {isCustomRange && (
        <Badge variant="secondary" className="text-xs">
          Live
        </Badge>
      )}
    </div>
  );
}
